import type { SalesOrderLineItem, SalesOrderShipmentSummary } from '@erp/rpc';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TroubleDataBadges } from '@/components/common/TroubleDataBadges';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { T, Var } from 'gt-react';
import { LineItemProductInfo } from '@/components/line-item-product-info/LineItemProductInfo';
import { Currency, formatCurrency } from '@/components/common/Currency';
import { MaxJoinsCell } from 'src/components/procurement/MaxJoinsCell';

function QtyCell({
  item,
  shipment,
}: {
  item: SalesOrderLineItem;
  shipment?: SalesOrderShipmentSummary | null;
}) {
  const qtyMismatch =
    item.supplierQuantity != null &&
    Number(item.supplierQuantity) !== Number(item.quantity);
  if (!qtyMismatch) {
    return <>{item.supplierQuantity ?? item.quantity ?? '-'}</>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40">
          {item.supplierQuantity}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium">
          <T>Finalized shipped quantity</T>
        </p>
        <p className="text-xs text-muted-foreground">
          <T>
            From fulfillment records (AR_TRX_LINE.QNT) — SO-stage qty:{' '}
            <Var>{item.quantity ?? '—'}</Var>
          </T>
          {shipment?.originLoadNumber ? (
            <>
              {' — '}
              <T>
                Load #<Var>{shipment.originLoadNumber}</Var>
              </T>
            </>
          ) : null}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

function SupplierCostCell({
  item,
  currency,
  shipment,
}: {
  item: SalesOrderLineItem;
  currency?: string | null;
  shipment?: SalesOrderShipmentSummary | null;
}) {
  const code = currency ?? 'USD';
  if (item.supplierCostPrice) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40">
            {formatCurrency(item.supplierCostPrice, { currency: code }) || '-'}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">
            <T>Finalized supplier cost</T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T context="sales order line item supplier cost source">
              From fulfillment records (AR_TRX_DETAIL)
            </T>
            {shipment?.originLoadNumber ? (
              <>
                {' — '}
                <T context="sales order line item load number">
                  Load #<Var>{shipment.originLoadNumber}</Var>
                </T>
              </>
            ) : null}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }
  if (item.costPrice) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-muted-foreground underline decoration-dotted decoration-muted-foreground/40">
            {formatCurrency(item.costPrice, { currency: code }) || '-'}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">
            <T>Estimated cost</T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T context="sales order line item estimated cost tooltip">
              SO-stage estimate — not yet finalized
            </T>
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }
  return <span className="text-muted-foreground">-</span>;
}

export function SalesOrderLineItemRow({
  item,
  index,
  salesOrderId,
  currency,
  shipment,
}: {
  item: SalesOrderLineItem;
  index: number;
  salesOrderId: string;
  currency?: string | null;
  shipment?: SalesOrderShipmentSummary | null;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono text-muted-foreground">
        {item.lineItemNumber ?? index + 1}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <LineItemProductInfo
            productId={item.productId}
            productVariantId={item.productVariantId}
            productName={item.productName}
            productSku={item.productSku}
            productVariantSku={item.productVariantSku}
          />
          {item.customerProductSku ? (
            <span className="text-xs text-muted-foreground">
              <T>
                Customer SKU: <Var>{item.customerProductSku}</Var>
              </T>
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <QtyCell item={item} shipment={shipment} />
      </TableCell>
      <TableCell className="text-right">
        <Currency
          value={item.unitPrice}
          currency={currency ?? 'USD'}
          fallback="-"
        />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <SupplierCostCell
            item={item}
            currency={currency}
            shipment={shipment}
          />
          <TroubleDataBadges
            troubleData={item.troubleData}
            legacyConsign={item.isConsignment ?? false}
          />
        </div>
      </TableCell>
      <TableCell className="text-right font-medium">
        <Currency
          value={item.lineTotal}
          currency={currency ?? 'USD'}
          fallback="-"
        />
      </TableCell>
      <TableCell>
        {item.rescheduledDate
          ? formatDateOnlyString(item.rescheduledDate)
          : '-'}
      </TableCell>
      <TableCell className="align-top">
        <MaxJoinsCell
          key={`${item.id}-${item.maxJoins ?? 'inherit'}`}
          salesOrderId={salesOrderId}
          line={item}
        />
      </TableCell>
    </TableRow>
  );
}
